<template>
  <div class="bi-control-panel-table-container pr-5">
    <v-card>
      <v-data-table
        class="bi-control-panel-table bi-control-panel-table--parameters bi-parameter-rules-table"
        :style="tableMaxHeightStyle"
        item-key="id"
        :headers="headers"
        :items="rows"
        :loading="tableLoading"
        :page="pagination.page + 1"
        :items-per-page="pagination.size"
        :server-items-length="pagination.totalElements"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        :no-data-text="$t('biControlPanel.parameterRules.table.noData')"
        mobile-breakpoint="0"
        fixed-header
        hide-default-header
        dense
        @update:page="onUpdatePage"
        @update:items-per-page="onUpdateItemsPerPage"
      >
        <template #header="{ props }">
          <thead class="v-data-table-header">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.value"
                :class="[header.class, header.align ? `text-${header.align}` : '']"
              >
                <div class="d-flex align-center" :class="{ 'justify-center': header.align === 'center' }">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="bi-header-text">{{ header.text }}</span>
                    </template>
                    <span>{{ header.text }}</span>
                  </v-tooltip>
                </div>
              </th>
            </tr>
          </thead>
        </template>

        <template #item.level="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.level.label }}</span>
            </template>
            <span>{{ item.level.label }}</span>
          </v-tooltip>
        </template>

        <template #item.levelOptionLabel="{ item }">
          <v-tooltip bottom :disabled="!item.levelOptionLabel">
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.levelOptionLabel }}</span>
            </template>
            <span>{{ item.levelOptionLabel }}</span>
          </v-tooltip>
        </template>

        <template #item.paymentModels="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ formatPaymentModels(item) }}</span>
            </template>
            <span>{{ formatPaymentModels(item) }}</span>
          </v-tooltip>
        </template>

        <template #item.parameterType="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.parameterType.label }}</span>
            </template>
            <span>{{ item.parameterType.label }}: {{ (item.parameterValue && item.parameterValue.label) || '-' }}</span>
          </v-tooltip>
        </template>

        <template #item.active="{ item }">
          <div class="d-flex align-center bi-control-panel-status-cell">
            <v-switch
              v-if="canEdit"
              :key="`status-${item.id}-${statusRenderKey}`"
              :input-value="item.active"
              color="primary"
              hide-details
              inset
              dense
              :disabled="isStatusUpdating(item.id)"
              :loading="isStatusUpdating(item.id)"
              class="status-switch mr-2 mt-0 pt-0"
              @change="updateStatus(item, $event)"
            />
            <span>
              {{ item.active
                ? $t('biControlPanel.parameterRules.table.active')
                : $t('biControlPanel.parameterRules.table.inactive') }}
            </span>
          </div>
        </template>

        <template #item.actions="{ item }">
          <BIParameterRuleRowActionsMenu
            :item="item"
            :can-edit="canEdit"
            @edit="$emit('edit', item)"
            @rule-history="$emit('rule-history', item)"
            @parameter-history="$emit('parameter-history', item)"
            @toggle-rule="toggleRuleStatus"
          />
        </template>
      </v-data-table>
    </v-card>
    <SnackbarCustomize ref="SnackbarCustomize" />
  </div>
</template>

<script>
import BIParameterRuleRowActionsMenu from '@/components/BIControlPanel/BIParameterRuleRowActionsMenu.vue';
import SnackbarCustomize from '@/components/CustomAlerts/SnackbarCustomize.vue';
import BiPanelRuleModule from '@/store/modules/bi-panel-rule/bi-panel-rule-module';

export default {
  name: 'BIParameterRulesTable',
  components: { BIParameterRuleRowActionsMenu, SnackbarCustomize },
  props: {
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      updatingStatusIds: [],
      updatingRuleIds: [],
      statusRenderKey: 0,
      wrapperMaxHeight: null,
    };
  },
  computed: {
    tableState() {
      return BiPanelRuleModule.table;
    },
    rows() {
      return this.tableState.rows;
    },
    tableLoading() {
      return this.tableState.loading;
    },
    pagination() {
      return this.tableState.pagination;
    },
    tableMaxHeightStyle() {
      if (!this.wrapperMaxHeight) return null;

      return { '--bi-table-max-height': `${this.wrapperMaxHeight}px` };
    },
    headers() {
      return [
        {
          text: this.translate('biControlPanel.parameterRules.table.levelType'),
          value: 'level',
          sortable: false,
          class: 'col-level-type',
          cellClass: 'col-level-type',
        },
        {
          text: this.translate('biControlPanel.parameterRules.table.levelOption'),
          value: 'levelOptionLabel',
          sortable: false,
          class: 'col-level-option',
          cellClass: 'col-level-option',
        },
        {
          text: this.translate('biControlPanel.parameterRules.table.paymentModel'),
          value: 'paymentModels',
          sortable: false,
          class: 'col-payment-model',
          cellClass: 'col-payment-model',
        },
        {
          text: this.translate('biControlPanel.parameterRules.table.rule'),
          value: 'parameterType',
          sortable: false,
          class: 'col-rule',
          cellClass: 'col-rule',
        },
        {
          text: this.translate('biControlPanel.parameterRules.table.ruleStatus'),
          value: 'active',
          sortable: false,
          class: 'col-rule-status',
          cellClass: 'col-rule-status',
        },
        {
          text: this.translate('biControlPanel.parameterRules.table.actions'),
          value: 'actions',
          sortable: false,
          align: 'center',
          class: 'col-actions',
          cellClass: 'col-actions',
        },
      ];
    },
  },
  watch: {
    rows() {
      this.$nextTick(() => {
        this.updateWrapperMaxHeight();
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateWrapperMaxHeight();
    });
    window.addEventListener('resize', this.updateWrapperMaxHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWrapperMaxHeight);
  },
  methods: {
    updateWrapperMaxHeight() {
      const wrapper = this.$el?.querySelector('.v-data-table__wrapper');

      if (!wrapper) return;

      const footer = this.$el.querySelector('.v-data-footer');
      const footerHeight = footer ? footer.offsetHeight : 0;
      const { top } = wrapper.getBoundingClientRect();
      const height = window.innerHeight - top - footerHeight - 20;

      this.wrapperMaxHeight = Math.max(240, Math.floor(height));
    },
    formatPaymentModels(item) {
      return (item.paymentModels || []).map((paymentModel) => paymentModel.label).join(', ');
    },
    isStatusUpdating(id) {
      return this.updatingStatusIds.includes(id);
    },
    setStatusUpdating(id, loading) {
      if (loading) {
        if (!this.isStatusUpdating(id)) {
          this.updatingStatusIds = [...this.updatingStatusIds, id];
        }
        return;
      }

      this.updatingStatusIds = this.updatingStatusIds.filter((itemId) => itemId !== id);
    },
    updateStatus(item, value) {
      const nextStatus = Boolean(value);

      if (item.active === nextStatus || this.isStatusUpdating(item.id)) {
        return;
      }

      this.setStatusUpdating(item.id, true);
      this.runStoreAction(
        BiPanelRuleModule.updateParameterStatus({ id: item.id, active: nextStatus }),
        this.translate('biControlPanel.parameterRules.messages.statusUpdateError'),
      ).then((result) => {
        // Ex.: 409 ao reativar com outro parâmetro ativo do mesmo tipo; recria o switch para voltar ao estado real
        if (result && result.success === false) this.statusRenderKey += 1;
      }).finally(() => {
        this.setStatusUpdating(item.id, false);
      });
    },
    toggleRuleStatus(item) {
      if (this.updatingRuleIds.includes(item.ruleId)) return;

      this.updatingRuleIds = [...this.updatingRuleIds, item.ruleId];
      this.runStoreAction(
        BiPanelRuleModule.updateRuleStatus({ ruleId: item.ruleId, active: !item.ruleActive }),
        this.translate('biControlPanel.parameterRules.messages.statusUpdateError'),
      ).finally(() => {
        this.updatingRuleIds = this.updatingRuleIds.filter((id) => id !== item.ruleId);
      });
    },
    onUpdatePage(page) {
      this.runStoreAction(
        BiPanelRuleModule.changeTablePage(page - 1),
        this.translate('biControlPanel.parameterRules.messages.searchError'),
      );
    },
    onUpdateItemsPerPage(size) {
      if (size <= 0) return;

      this.runStoreAction(
        BiPanelRuleModule.changeTableSize(size),
        this.translate('biControlPanel.parameterRules.messages.searchError'),
      );
    },
    translate(key) {
      return this.$t(key).toString();
    },
    showError(message) {
      this.$refs.SnackbarCustomize.open('error', message);
    },
    async runStoreAction(actionPromise, fallbackMessage) {
      const result = await actionPromise;

      if (result && result.success === false) {
        this.showError(result.errorMessage || fallbackMessage);
      }

      return result;
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/biControlPanel/parameterRulesTable.scss";
</style>
